import React, { useState, useEffect } from 'react';
import './HomePageEditor.css';
import ImageInput from './ImageInput';

const API_URL = process.env.REACT_APP_API_URL || '';

const HomePageEditor = () => {
  const [form, setForm] = useState({
    heroTitle: '',
    heroSubtitle: '',
    ctaButtonText: '',
    ctaButtonLink: '/services',
    heroImage: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/homepage`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setForm({
        heroTitle: data.heroTitle || '',
        heroSubtitle: data.heroSubtitle || '',
        ctaButtonText: data.ctaButtonText || '',
        ctaButtonLink: data.ctaButtonLink || '/services',
        heroImage: data.heroImage || '',
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load home page content' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/homepage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) throw new Error('Failed to save');

      setMessage({ type: 'success', text: 'Home page updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update home page' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="homepage-editor">
      <div className="editor-header">
        <h2>Edit Home Page</h2>
        <p>Update the hero section content on your home page</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="editor-form">
        <div className="form-group">
          <label htmlFor="heroTitle">Hero Title</label>
          <input
            type="text"
            id="heroTitle"
            name="heroTitle"
            value={form.heroTitle}
            onChange={handleChange}
            placeholder="Welcome to Audrey's Handcrafted Collection"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="heroSubtitle">Hero Subtitle</label>
          <input
            type="text"
            id="heroSubtitle"
            name="heroSubtitle"
            value={form.heroSubtitle}
            onChange={handleChange}
            placeholder="Elegant jewelry and crafts made with love"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ctaButtonText">Call-to-Action Button Text</label>
          <input
            type="text"
            id="ctaButtonText"
            name="ctaButtonText"
            value={form.ctaButtonText}
            onChange={handleChange}
            placeholder="Explore Collection"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ctaButtonLink">Call-to-Action Button Link</label>
          <select
            id="ctaButtonLink"
            name="ctaButtonLink"
            value={form.ctaButtonLink}
            onChange={handleChange}
          >
            <option value="/services">Services (/services)</option>
            <option value="/contact">Contact (/contact)</option>
            <option value="/shop">Shop (/shop)</option>
          </select>
        </div>

        <div className="form-group">
          <ImageInput label="Hero Image" value={form.heroImage} onChange={(v) => setForm(prev => ({ ...prev, heroImage: v }))} />
        </div>

        <div className="form-actions">
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="preview-section">
        <h3>Preview</h3>
        <div className="preview-hero">
          {form.heroImage && (
            <div className="preview-hero-image">
              <img src={form.heroImage} alt="Hero" />
            </div>
          )}
          <h1>{form.heroTitle}</h1>
          <p>{form.heroSubtitle}</p>
          <button className="preview-cta">{form.ctaButtonText}</button>
        </div>
      </div>
    </div>
  );
};

export default HomePageEditor;
